/**
 * Panel de administración de peticiones para reponedores
 */
import React, { useState, useEffect, useContext, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    useTheme,
    useMediaQuery,
    Box,
    Card,
    Typography,
    Divider
} from "@mui/material";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";

import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { StateContext } from "../../../components/context/StateProvider";
import RequierePermisos from "../../../components/auth/RequierePermisos";
import PeticionesService from "../api/peticiones.api";
import UsuariosService from "../../../services/usuarios.service";
import Toasts from "../../../components/notificacion/Toasts";
import { PETICION_ESTADO, PETICION_TIPO } from "../constants";
import { generatePanelPeticionesColumns } from "../helpers/columnsPanel";
import { ModalAsignar, ModalDesasignar, ModalMarcarPreparado, ModalTraspasar, PeticionesMobileList, FiltrosPeticiones, DrawerProducto, BADGE_COLORS } from "../components";

// Valores iniciales de fechas para los filtros
const getFechaInicioInicial = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const getFechaFinInicial = () => new Date().toISOString().slice(0, 10);

// Aspecto común del DataGrid más el color de fondo según el tipo de petición
const SX_GRID = {
    ...DATA_GRID_SX,
    "& .row-tipo-peticion": { backgroundColor: "rgba(255, 243, 224, 0.15)" },
    "& .row-tipo-reposicion": { backgroundColor: "rgba(227, 242, 253, 0.15)" },
};

const PanelPeticiones = () => {
    const { state, updateState, puedeAccederPanelPeticiones, getNombreAlmacen } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [peticiones, setPeticiones] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    // processingAction guarda la combinación id-acción que está en curso,
    // impidiendo dobles clicks y aportando feedback visual específico en cada botón.
    const [processingAction, setProcessingAction] = useState(null);

    // Estados de filtros
    const [fechaInicio, setFechaInicio] = useState(getFechaInicioInicial);
    const [fechaFin, setFechaFin] = useState(getFechaFinInicial);
    const [empleado, setEmpleado] = useState('');
    const [tipo, setTipo] = useState('');
    const [q, setQ] = useState('');
    // Filtros aplicados (separados de inputs para aplicar solo al hacer click)
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        fechaInicio: getFechaInicioInicial(),
        fechaFin: getFechaFinInicial(),
        estado: '',
        empleado: '',
        tipo: '',
        q: ''
    });

    // Modales de confirmación
    const [showModalAsignar, setShowModalAsignar] = useState(false);
    const [peticionToAssign, setPeticionToAssign] = useState(null);
    const [showModalDesasignar, setShowModalDesasignar] = useState(false);
    const [peticionToUnassign, setPeticionToUnassign] = useState(null);
    const [showModalPreparado, setShowModalPreparado] = useState(false);
    const [peticionToComplete, setPeticionToComplete] = useState(null);
    const [showModalTraspasar, setShowModalTraspasar] = useState(false);
    const [peticionToTransfer, setPeticionToTransfer] = useState(null);

    // Estado del drawer de producto
    const [drawerProductoOpen, setDrawerProductoOpen] = useState(false);
    const [codigoProductoSeleccionado, setCodigoProductoSeleccionado] = useState(null);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [datos, emp] = await Promise.all([
                PeticionesService.list(),
                UsuariosService.listarUsuarios()
            ]);
            setPeticiones(datos);
            setEmpleados(emp ?? []);
            setLoading(false);
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
            setLoading(false);
        }
    };

    // Recarga solo la lista de peticiones, sin re-verificar el acceso.
    // Se llama tras cada accion de reponedor exitosa para reflejar el nuevo estado.
    const cargarPeticiones = async () => {
        try {
            const datos = await PeticionesService.list();
            setPeticiones(datos);
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        }
    };

    const handleAsignarme = (peticionId) => {
        const peticion = peticiones.find(p => p.id === peticionId);
        if (!peticion) return;

        setPeticionToAssign(peticion);
        setShowModalAsignar(true);
    };

    const cerrarModalAsignar = () => {
        setShowModalAsignar(false);
        setPeticionToAssign(null);
    };

    const confirmarAsignacion = async () => {
        if (!peticionToAssign) return;

        setProcessingAction(`${peticionToAssign.id}-asignar`);
        try {
            await PeticionesService.asignar(peticionToAssign.id);
            const toast = Toasts.generateToast('Petición asignada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
            cerrarModalAsignar();
            await cargarPeticiones();
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setProcessingAction(null);
        }
    };

    const handleDesasignar = (peticionId) => {
        const peticion = peticiones.find(p => p.id === peticionId);
        if (!peticion) return;

        setPeticionToUnassign(peticion);
        setShowModalDesasignar(true);
    };

    const cerrarModalDesasignar = () => {
        setShowModalDesasignar(false);
        setPeticionToUnassign(null);
    };

    const confirmarDesasignacion = async () => {
        if (!peticionToUnassign) return;

        setProcessingAction(`${peticionToUnassign.id}-desasignar`);
        try {
            await PeticionesService.desasignar(peticionToUnassign.id);
            const toast = Toasts.generateToast('Petición desasignada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
            cerrarModalDesasignar();
            await cargarPeticiones();
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setProcessingAction(null);
        }
    };

    const handleMarcarPreparado = (peticionId) => {
        const peticion = peticiones.find(p => p.id === peticionId);
        if (!peticion) return;

        setPeticionToComplete(peticion);
        setShowModalPreparado(true);
    };

    const cerrarModalPreparado = () => {
        setShowModalPreparado(false);
        setPeticionToComplete(null);
    };

    const confirmarMarcarPreparado = async () => {
        if (!peticionToComplete) return;

        setProcessingAction(`${peticionToComplete.id}-preparado`);
        try {
            await PeticionesService.marcarPreparado(peticionToComplete.id);
            const toast = Toasts.generateToast('Petición marcada como preparada', 'success');
            updateState({ toasts: [...state.toasts, toast] });
            cerrarModalPreparado();
            await cargarPeticiones();
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setProcessingAction(null);
        }
    };

    const handleTraspasar = (peticionId) => {
        const peticion = peticiones.find(p => p.id === peticionId);
        if (!peticion) return;

        setPeticionToTransfer(peticion);
        setShowModalTraspasar(true);
    };

    const cerrarModalTraspasar = () => {
        setShowModalTraspasar(false);
        setPeticionToTransfer(null);
    };

    const confirmarTraspasar = async () => {
        if (!peticionToTransfer) return;

        setProcessingAction(`${peticionToTransfer.id}-traspasar`);
        try {
            await PeticionesService.traspasar(peticionToTransfer.id);
            const toast = Toasts.generateToast('Petición traspasada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
            cerrarModalTraspasar();
            await cargarPeticiones();
        } catch (error) {
            const toast = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setProcessingAction(null);
        }
    };

    const handleFiltrar = () => {
        setFiltrosAplicados(prev => ({
            ...prev,
            fechaInicio,
            fechaFin,
            empleado,
            tipo,
            q
        }));
    };

    const handleLimpiar = () => {
        setFechaInicio(getFechaInicioInicial());
        setFechaFin(getFechaFinInicial());
        setEmpleado('');
        setTipo('');
        setQ('');
        setFiltrosAplicados({
            fechaInicio: getFechaInicioInicial(),
            fechaFin: getFechaFinInicial(),
            estado: '',
            empleado: '',
            tipo: '',
            q: ''
        });
    };

    const handleEstadisticaClick = (estadoValor) => {
        // Si ya está activo ese filtro, lo quita
        if (filtrosAplicados.estado === estadoValor) {
            setFiltrosAplicados(prev => ({ ...prev, estado: '' }));
        } else {
            // Aplica el nuevo filtro
            setFiltrosAplicados(prev => ({ ...prev, estado: estadoValor }));
        }
    };

    const handleReferenciaClick = (codigoProducto) => {
        setCodigoProductoSeleccionado(codigoProducto);
        setDrawerProductoOpen(true);
    };

    const cerrarDrawerProducto = () => {
        setDrawerProductoOpen(false);
        setCodigoProductoSeleccionado(null);
    };

    // Peticiones filtradas
    const peticionesFiltradas = useMemo(() => {
        let filtradas = [...peticiones];

        // Filtro por fechas
        if (filtrosAplicados.fechaInicio) {
            const fechaInicioFiltro = new Date(filtrosAplicados.fechaInicio);
            fechaInicioFiltro.setHours(0, 0, 0, 0);
            filtradas = filtradas.filter(p => {
                const fechaPeticion = p.creado ? new Date(p.creado) : null;
                return fechaPeticion && fechaPeticion >= fechaInicioFiltro;
            });
        }

        if (filtrosAplicados.fechaFin) {
            const fechaFinFiltro = new Date(filtrosAplicados.fechaFin);
            fechaFinFiltro.setHours(23, 59, 59, 999);
            filtradas = filtradas.filter(p => {
                const fechaPeticion = p.creado ? new Date(p.creado) : null;
                return fechaPeticion && fechaPeticion <= fechaFinFiltro;
            });
        }

        // Filtro por estado
        if (filtrosAplicados.estado) {
            filtradas = filtradas.filter(p => p.estado === filtrosAplicados.estado);
        }

        // Filtro por empleado (reponedor asignado)
        if (filtrosAplicados.empleado) {
            filtradas = filtradas.filter(p =>
                p.reponedor && String(p.reponedor.id) === String(filtrosAplicados.empleado)
            );
        }

        // Filtro por tipo
        if (filtrosAplicados.tipo) {
            filtradas = filtradas.filter(p => (p.tipo || PETICION_TIPO.PETICION.value) === filtrosAplicados.tipo);
        }

        // Filtro por búsqueda texto
        if (filtrosAplicados.q) {
            const searchLower = filtrosAplicados.q.toLowerCase();
            filtradas = filtradas.filter(p => {
                return (
                    p.nombreProducto?.toLowerCase().includes(searchLower) ||
                    p.referencia?.toLowerCase().includes(searchLower) ||
                    p.numeroAlbaran?.toLowerCase().includes(searchLower) ||
                    p.usuario?.username?.toLowerCase().includes(searchLower) ||
                    p.reponedor?.username?.toLowerCase().includes(searchLower)
                );
            });
        }

        return filtradas;
    }, [peticiones, filtrosAplicados]);

    // Estadísticas de peticiones (sobre datos filtrados)
    const estadisticas = useMemo(() => {
        const noAtendidas = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.NO_ATENDIDO.value).length;
        const enProceso = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.EN_PROCESO.value).length;
        const preparadas = peticionesFiltradas.filter(p => p.estado === PETICION_ESTADO.PREPARADO.value).length;

        return { noAtendidas, enProceso, preparadas, total: peticionesFiltradas.length };
    }, [peticionesFiltradas]);

    const columns = generatePanelPeticionesColumns({
        getNombreAlmacen,
        processingAction,
        onAsignarme: handleAsignarme,
        onDesasignar: handleDesasignar,
        onMarcarPreparado: handleMarcarPreparado,
        onTraspasar: handleTraspasar,
        onReferenciaClick: handleReferenciaClick,
    });

    return (
        <RequierePermisos
            verificacionPersonalizada={puedeAccederPanelPeticiones}
            mensajeError="No tienes acceso a este panel de reponedores"
        >
            <Box id="panel-peticiones" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box>
                        <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <RequestPageIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                            Panel de peticiones
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Gestiona las solicitudes de productos del almacén
                        </Typography>
                    </Box>

                    {/* Estadísticas */}
                    {!loading && peticiones.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, 1fr)",
                                        sm: "repeat(4, 1fr)",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                {/* No atendidas */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.NO_ATENDIDO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.RED.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.NO_ATENDIDO.value ? `2px solid ${BADGE_COLORS.RED.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <WarningAmberIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.RED.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            No atendidas
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.RED.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.noAtendidas}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* En proceso */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.EN_PROCESO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.AMBER.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.EN_PROCESO.value ? `2px solid ${BADGE_COLORS.AMBER.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <HourglassEmptyIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.AMBER.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            En proceso
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.AMBER.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.enProceso}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Preparadas */}
                                <Box
                                    onClick={() => handleEstadisticaClick(PETICION_ESTADO.PREPARADO.value)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.GREEN.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtrosAplicados.estado === PETICION_ESTADO.PREPARADO.value ? `2px solid ${BADGE_COLORS.GREEN.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.GREEN.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Preparadas
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.GREEN.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.preparadas}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Total */}
                                <Box
                                    onClick={() => handleEstadisticaClick('')}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: BADGE_COLORS.BLUE.bg,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: !filtrosAplicados.estado ? `2px solid ${BADGE_COLORS.BLUE.border}` : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <InventoryIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: BADGE_COLORS.BLUE.text,
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: BADGE_COLORS.BLUE.text,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Total
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: BADGE_COLORS.BLUE.text,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Filtros */}
                    {!loading && peticiones.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <FiltrosPeticiones
                                fechaInicio={fechaInicio}
                                setFechaInicio={setFechaInicio}
                                fechaFin={fechaFin}
                                setFechaFin={setFechaFin}
                                empleado={empleado}
                                setEmpleado={setEmpleado}
                                tipo={tipo}
                                setTipo={setTipo}
                                q={q}
                                setQ={setQ}
                                empleadosOptions={empleados}
                                onFiltrar={handleFiltrar}
                                onLimpiar={handleLimpiar}
                                loading={loading}
                                totalResultados={peticionesFiltradas.length}
                                fechaInicioInicial={getFechaInicioInicial()}
                                fechaFinInicial={getFechaFinInicial()}
                            />
                        </>
                    )}
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {!isMobile ? (
                        <DataGrid
                            rows={peticionesFiltradas}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            getRowClassName={(params) => {
                                const tipo = params.row.tipo || PETICION_TIPO.PETICION.value;
                                return tipo === PETICION_TIPO.REPOSICION.value ? 'row-tipo-reposicion' : 'row-tipo-peticion';
                            }}
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            disableRowSelectionOnClick
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={SX_GRID}
                        />
                    ) : (
                        <PeticionesMobileList
                            rows={peticionesFiltradas}
                            loading={loading}
                            onAsignarme={handleAsignarme}
                            onDesasignar={handleDesasignar}
                            onMarcarPreparado={handleMarcarPreparado}
                            onTraspasar={handleTraspasar}
                            onReferenciaClick={handleReferenciaClick}
                            processingAction={processingAction}
                            getNombreAlmacen={getNombreAlmacen}
                        />
                    )}
                </Box>
            </Card>

            {/* Modal de confirmación de asignación */}
            <ModalAsignar
                show={showModalAsignar}
                onHide={cerrarModalAsignar}
                peticion={peticionToAssign}
                onConfirm={confirmarAsignacion}
                loading={processingAction !== null}
            />

            {/* Modal de confirmación de desasignación */}
            <ModalDesasignar
                show={showModalDesasignar}
                onHide={cerrarModalDesasignar}
                peticion={peticionToUnassign}
                onConfirm={confirmarDesasignacion}
                loading={processingAction !== null}
            />

            {/* Modal de confirmación de marcar preparado */}
            <ModalMarcarPreparado
                show={showModalPreparado}
                onHide={cerrarModalPreparado}
                peticion={peticionToComplete}
                onConfirm={confirmarMarcarPreparado}
                loading={processingAction !== null}
            />

            {/* Modal de confirmación de traspaso */}
            <ModalTraspasar
                show={showModalTraspasar}
                onHide={cerrarModalTraspasar}
                peticion={peticionToTransfer}
                onConfirm={confirmarTraspasar}
                loading={processingAction !== null}
            />

            {/* Drawer de información del producto */}
            <DrawerProducto
                open={drawerProductoOpen}
                onClose={cerrarDrawerProducto}
                codigoProducto={codigoProductoSeleccionado}
                getNombreAlmacen={getNombreAlmacen}
            />
        </Box>
        </RequierePermisos>
    );
};

export default PanelPeticiones;
